import {
  type MessagePathPart,
  MessagePathPartType,
} from "@/shared/domain/topics";

// Escape path separators (and the escape char itself) so a "." or "[" "]"
// inside a field name can't masquerade as path structure.
function escapeAttribute(attribute: string): string {
  return attribute.replace(/[\\.[\]]/g, (ch) => `\\${ch}`);
}

/**
 * Collision-free identity key for a message path — used in Log.data, the
 * column-filter map, merge dedup, and the add-overlap gate.
 *
 * Distinct from MessagePathNode.partsToString, the *display* form, which joins
 * attributes on "." without escaping: there a field named "a.b" collides with a
 * nested path a -> b. This key escapes those separators, so distinct paths get
 * distinct keys; it equals partsToString when no attribute contains them.
 */
export function columnIdForParts(parts: MessagePathPart[]): string {
  return parts.reduce((key, part) => {
    if (part.type === MessagePathPartType.Attr) {
      const separator = key.length > 0 ? "." : "";
      return `${key}${separator}${escapeAttribute(part.attribute)}`;
    }
    // end may be undefined, null, or Infinity — all mean "open"; collapse them
    // to one key so the same slice merges regardless of which form it arrives in.
    const end = part.end ?? Infinity;
    return `${key}[${part.start}:${end === Infinity ? "" : end}]`;
  }, "");
}
